import { useResetPassword } from "@/services/mutations/auth";
import { useValidateResetToken } from "@/services/queries/auth";
import { Hand } from "@/components/icons";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormMessage,
  FormItem,
  FormControl,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Check, Fingerprint, X } from "@phosphor-icons/react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Mixpanel } from "@/lib/mixpanel";
import { useState } from "react";
import { Loader2 } from "lucide-react";

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [error, setError] = useState<string | null>(null);

  const {
    data: tokenValidation,
    isLoading: isTokenValidating,
    isError: isTokenError,
  } = useValidateResetToken({ token });

  const {
    mutate: resetPassword,
    isPending,
    isSuccess,
  } = useResetPassword({
    onSuccess: () => {
      Mixpanel.track("Reset Password Submitted Successfully");
    },
    onError: () => {
      setError(t("error_message.something_went_wrong"));
      Mixpanel.track("Reset Password Submitted Failed");
    },
  });

  const resetPasswordSchema = z
    .object({
      password: z.string().min(8, {
        message: t("error_message.minimum_characters", { count: 8 }),
      }),
      confirmPassword: z.string().min(8, {
        message: t("error_message.minimum_characters", { count: 8 }),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("error_message.passwords_do_not_match"),
      path: ["confirmPassword"],
    });

  type FormValues = { password: string; confirmPassword: string };

  const form = useForm<FormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = (data: FormValues) => {
    setError(null);
    resetPassword({ token, newPassword: data.password });
  };

  const isTokenInvalid =
    !isTokenValidating && (isTokenError || !tokenValidation?.valid);
  const isTokenValid = !isTokenValidating && tokenValidation?.valid === true;
  const isButtonDisabled = isTokenValidating || isPending || !isTokenValid;

  if (isTokenValidating) {
    return (
      <>
        <title>{t("title.reset_password")}</title>
        <div className="flex h-screen items-center justify-center bg-[url('/images/wave-bg.png')] bg-cover bg-center bg-no-repeat">
          <Loader2 className="h-20 w-20 animate-spin text-white" />
        </div>
      </>
    );
  }

  if (isSuccess) {
    return (
      <>
        <title>{t("title.reset_password")}</title>
        <div className="flex h-screen items-center justify-center bg-[url('/images/wave-bg.png')] bg-cover bg-center bg-no-repeat">
          <div className="flex h-full w-full max-w-md flex-col items-center justify-between px-5 py-10">
            <span className="h-[38px]" />
            <PasswordResetSuccess />
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="mt-4 w-full"
            >
              {t("button.login")}
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (isTokenInvalid) {
    return (
      <>
        <title>{t("title.reset_password")}</title>
        <div className="flex h-screen items-center justify-center bg-[url('/images/wave-bg.png')] bg-cover bg-center bg-no-repeat">
          <div className="flex h-full w-full max-w-md flex-col items-center justify-between px-5 py-10">
            <span className="h-[38px]" />
            <TokenError />
            <Button
              onClick={() => navigate("/login")}
              variant="outline"
              className="mt-4 w-full"
            >
              {t("button.login")}
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <title>{t("title.reset_password")}</title>

      <div className="flex h-screen items-center justify-center bg-[url('/images/wave-bg.png')] bg-cover bg-center bg-no-repeat">
        <div className="flex h-full w-full max-w-md flex-col items-center justify-between px-5 py-10 text-3xl font-bold">
          <span className="h-[38px]" />
          <div className="flex w-full flex-col items-center">
            <Hand size={56} />
            <span className="mb-8 mt-2 text-[26px] leading-8 text-white">
              {t("title.reset_password")}
            </span>

            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="w-full space-y-3"
              >
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          invalid={!!form.formState.errors.password}
                          autoComplete="off"
                          id="password"
                          placeholder={t("label.password")}
                          type="password"
                          showHidePassword={true}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage data-testid="password-error" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Input
                          invalid={!!form.formState.errors.confirmPassword}
                          autoComplete="off"
                          id="confirmPassword"
                          placeholder={t("label.password_repeat")}
                          type="password"
                          showHidePassword={true}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage data-testid="confirm-password-error" />
                    </FormItem>
                  )}
                />

                {error && (
                  <p
                    data-testid="reset-password-error"
                    className="mx-auto !mt-4 text-center text-[13px] font-medium leading-[17px] text-[#E86363]"
                  >
                    {error}
                  </p>
                )}

                <Button
                  isPending={isPending || isTokenValidating}
                  disabled={isButtonDisabled}
                  className="w-full"
                  type="submit"
                >
                  {t("button.reset_password")}
                </Button>
              </form>
            </Form>
          </div>

          <Button
            onClick={() => navigate("/login")}
            variant="outline"
            className="mt-4 w-full"
          >
            {t("button.login")}
          </Button>
        </div>
      </div>
    </>
  );
};

const TokenError = () => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full max-w-sm flex-col items-center justify-center">
      <div className="relative w-fit">
        <Fingerprint color="#719BAD" size={40} />
        <span className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-[#E86363]">
          <X color="#fff" size={10} weight="bold" />
        </span>
      </div>

      <span className="mb-4 mt-2 max-w-[224px] text-center text-[26px] leading-8 text-white">
        {t("error_message.invalid_reset_token")}
      </span>

      <span className="mb-6 mt-2 w-full max-w-[330px] text-center leading-6 tracking-wide text-[#98B2D3]">
        {t("error_message.invalid_reset_token_description")}
      </span>
    </div>
  );
};

const PasswordResetSuccess = () => {
  const { t } = useTranslation();

  return (
    <div className="flex w-full max-w-sm flex-col items-center justify-center">
      <div className="relative w-fit">
        <Fingerprint color="#719BAD" size={40} />
        <span className="absolute right-0 top-0 flex h-4 w-4 items-center justify-center rounded-full bg-[#0DB873]">
          <Check color="#fff" size={10} weight="bold" />
        </span>
      </div>

      <span className="mb-4 mt-2 max-w-[224px] text-center text-[26px] leading-8 text-white">
        {t("success_message.reset_password_title")}
      </span>

      <span className="mb-6 mt-2 w-full max-w-[330px] text-center leading-6 tracking-wide text-[#98B2D3]">
        {t("success_message.reset_password_description")}
      </span>
    </div>
  );
};

export default ResetPassword;
